import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NovaButton } from '@rds-nova/angular-button';
import { NovaIcon, NovaIconRegistry } from '@rds-nova/angular-icon';
import { map } from 'rxjs/operators';
import {
  NovaTabs,
  NovaTabsItem,
  NovaTabsItemContent,
  NovaTabsList,
} from '@rds-nova/angular-tabs';
import { RonMonitorStore } from './+state/ron-monitor.store';
import { MonitorDatePickerComponent } from './components/monitor-date-picker/monitor-date-picker.component';
import { NotaryGridComponent } from './components/notary-grid/notary-grid.component';
import { SummaryCardsComponent } from './components/summary-cards/summary-cards.component';
import { RonMonitorDataSource } from './models/ron-monitor.data-source';
import { BoardSegmentFilter, ClosingPill } from './models/ron-monitor.models';
import { MockRonMonitorDataSource } from './services/mock-ron-monitor.data-source';

const SEGMENT_TABS: readonly BoardSegmentFilter[] = ['all', 'tsa', 'preferred'];

@Component({
  selector: 'app-ron-closing-monitor',
  templateUrl: './ron-closing-monitor.component.html',
  styleUrls: ['./ron-closing-monitor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NovaButton,
    NovaIcon,
    NovaIconRegistry,
    NovaTabs,
    NovaTabsItem,
    NovaTabsItemContent,
    NovaTabsList,
    MonitorDatePickerComponent,
    NotaryGridComponent,
    SummaryCardsComponent,
  ],
  providers: [
    { provide: RonMonitorDataSource, useClass: MockRonMonitorDataSource },
    RonMonitorStore,
  ],
})
export class RonClosingMonitorComponent {
  private readonly store = inject(RonMonitorStore);

  readonly date$ = this.store.date$;
  readonly summary$ = this.store.summary$;
  readonly lanes$ = this.store.lanes$;
  readonly closings$ = this.store.closings$;
  readonly boardTime$ = this.store.lastRefreshedAt$.pipe(
    map((value) => (value ? new Date(value) : null))
  );
  readonly segment$ = this.store.segment$;
  readonly searchTerm$ = this.store.searchTerm$;
  readonly expandedPackageIds$ = this.store.expandedPackageIds$;
  readonly details$ = this.store.details$;

  refresh(): void {
    this.store.refresh();
  }

  onDateChange(date: Date): void {
    this.store.setDate(date);
  }

  onSegmentChange(value: string): void {
    const segment = SEGMENT_TABS.find((tab) => tab === value);
    if (segment) {
      this.store.setSegment(segment);
    }
  }

  onSearch(term: string): void {
    this.store.setSearchTerm(term);
  }

  onPillActivate(closing: ClosingPill): void {
    this.store.toggleExpanded(closing.packageId);
  }

  onPanelRetry(packageId: number): void {
    this.store.retryDetail(packageId);
  }

  onPanelDismiss(packageId: number): void {
    this.store.collapse(packageId);
  }
}
